import { useGetSession } from '@/features/auth';
import { useGetActiveOrganization } from '@/features/organization/api';
import { useClaireDesigner } from '@/features/public-sites/claire-designer-flag';
import { approveDesignerHandoff } from '@/features/public-sites/designer-handoff';
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ComposerDecision } from '../composer-gate-context';
import { DecisionFooter } from './decision-footer';
export function WebsiteDesignerApprovalCard({
  id,
  request,
}: {
  id: string;
  request: {
    organizationId: string;
    locationId: string;
    request: string;
    locationName?: string;
  };
}) {
  const { enabled, loading } = useClaireDesigner();
  const { data: organization } = useGetActiveOrganization();
  const { user } = useGetSession();
  const navigate = useNavigate();
  const [answered, setAnswered] = useState(false);
  const accepting = useRef(false);
  if (loading || !enabled || organization?.id !== request.organizationId)
    return null;
  const canManage = user?.orgRole === 'admin' || user?.orgRole === 'owner';
  const accept = () => {
    if (!canManage || accepting.current || answered) return;
    accepting.current = true;
    try {
      const handoff = approveDesignerHandoff(request);
      void navigate({
        to: '/dashboard/l/$locationId/public-site',
        params: { locationId: request.locationId },
        search: { handoff },
      }).catch(() => {
        accepting.current = false;
        setAnswered(false);
        toast.error('Claire Designer could not be opened. Try again.');
      });
      setAnswered(true);
    } catch {
      accepting.current = false;
      toast.error('Your request could not be forwarded. Try again.');
    }
  };
  const decision = {
    sentence: 'I’ll open Claire Designer and pass along this request.',
    acceptLabel: 'Open in Claire Designer',
    rejectLabel: 'Not now',
    onAccept: accept,
    onReject: () => setAnswered(true),
    acceptDisabled: !canManage,
    acceptDisabledReason: !canManage
      ? 'An owner or admin can approve website changes.'
      : undefined,
  };
  return (
    <ComposerDecision id={id} gate={answered ? null : decision}>
      <div
        className="w-full rounded-lg border bg-card p-4 sm:max-w-md"
        data-testid="website-designer-approval"
      >
        <p className="text-sm font-medium">
          {request.locationName
            ? `${request.locationName} website`
            : 'Website change'}
        </p>
        <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
          {request.request}
        </p>
        {answered ? (
          <p className="mt-3 text-xs text-muted-foreground">Request handled</p>
        ) : (
          <DecisionFooter {...decision} />
        )}
      </div>
    </ComposerDecision>
  );
}
